import { useEffect, useMemo } from 'react';
import {
  ApiErrorText,
  // Button,
  ModalComponentWrapper,
} from '@/shared/components';
// import HistoryIcon from '@/shared/assets/history-icon.svg?react';
import {
  useWithdrawBankTransferForm,
  type WithdrawBankTransferState,
} from '../model';
import { useModalStoreState, useOperationsPreSelectAsset } from '@/shared/lib';
import {
  Footer,
  useWithdrawBankTransferModals,
  WithdrawBankTransferForm,
} from '@/features/withdraw-bank-transfer';
import { cn } from '@/shared/utils';
import type { OperationModalProps } from '@/shared/types';
import {
  BANK_ADDRESS_PARAM_IDS,
  type SavedBankAddress,
} from '@/entities/bank-address';
import { useCountriesDictionary } from '@/shared/api';

export const WithdrawBankTransferModal = ({
  assetCode,
  onBack,
  title,
  onEditSavedBankAddress,
}: OperationModalProps & {
  onEditSavedBankAddress: (savedBankAddress: SavedBankAddress) => void;
}) => {
  const {
    setMultipleState,
    setCloseConfirmationRequired,
    setSkipBackButtonConfirmation,
  } = useModalStoreState<WithdrawBankTransferState>();
  const { openConfirmationModal } = useWithdrawBankTransferModals();

  const { countriesData } = useCountriesDictionary();

  const {
    form,
    assetOptions,
    savedBankAddresses,
    selectedCurrency,
    hasWithdrawFee,
    totalAmount,
    isFromDataLoading,
    isFormDataError,
    formDataError,
    commissionData,
    handleFormSubmit,
    setTemplateId,
    error,
    handleMaxClick,
    isLoading,
    accountNumber,
    isFormDirty,
    formStepsData,
    currentStepIndex,
    handleFormStepBack,
    isSubmittingStep,
    isFinalStep,
    getCurrentStepData,
  } = useWithdrawBankTransferForm({
    onValidateSuccess: () => {
      openConfirmationModal();
    },
  });

  useOperationsPreSelectAsset({
    assetCode,
    options: assetOptions,
    setValue: code => {
      setTimeout(() => {
        form.setValue('currencyCode', code);
      }, 0);
    },
    isDisabled: isFromDataLoading,
  });

  useEffect(() => {
    setCloseConfirmationRequired(isFormDirty);
  }, [isFormDirty, setCloseConfirmationRequired]);

  useEffect(() => {
    const isFirstStep = currentStepIndex === 0;
    setSkipBackButtonConfirmation(!isFirstStep);
  }, [currentStepIndex, setSkipBackButtonConfirmation]);

  // const onHistoryClickHandler = () => {
  //   const { updatedSteps, formValues } = getCurrentStepData('current');

  //   setMultipleState({
  //     selectedCurrency,
  //     withdrawBankTransferFormData: formValues,
  //     accountNumber,
  //     formStepsData: updatedSteps,
  //     currentStepIndex,
  //   });

  //   onHistoryClick(
  //     selectedCurrency?.code ? { assetCode: selectedCurrency?.code } : undefined
  //   );
  // };

  const onStepBack = () => {
    if (currentStepIndex === 0) {
      return onBack();
    }

    handleFormStepBack();
  };

  const handleEditSavedBankAddress = (savedBankAddress: SavedBankAddress) => {
    const { updatedSteps, formValues } = getCurrentStepData('current');

    setMultipleState({
      selectedCurrency,
      withdrawBankTransferFormData: formValues,
      accountNumber,
      formStepsData: updatedSteps,
      currentStepIndex,
    });

    onEditSavedBankAddress(savedBankAddress);
  };

  const bankAddressOptions = useMemo(() => {
    if (!selectedCurrency) return savedBankAddresses;

    return savedBankAddresses.filter(address =>
      address.params.some(
        param =>
          param.paramId === BANK_ADDRESS_PARAM_IDS.currencyCode &&
          param.paramValue === selectedCurrency.code
      )
    );
  }, [selectedCurrency, savedBankAddresses]);

  return (
    <ModalComponentWrapper
      title={title}
      onBack={onStepBack}
      // actions={
      //   <Button
      //     variant="secondary"
      //     onClick={onHistoryClickHandler}
      //     className="flex items-center justify-center size-[4.2rem] bg-muted hover:bg-muted/80 bg-transparent-bg transition-colors text-foreground shadow-none self-start rounded-full border border-border mr-[0.8rem]"
      //     aria-label="history">
      //     <HistoryIcon className="size-[2.4rem] text-foreground" />
      //   </Button>
      // }
      footerClassName={cn(isFinalStep && 'sm:px-0 px-0 before:invisible')}
      footer={
        <Footer
          isFinalStep={isFinalStep}
          isLoading={isFromDataLoading || isLoading || isSubmittingStep}
          onSubmit={handleFormSubmit}
          feeAmount={commissionData?.feeAmount || 0}
          form={form}
          stepData={formStepsData[currentStepIndex]}
          totalAmount={totalAmount}
        />
      }>
      <WithdrawBankTransferForm
        form={form}
        setTemplateId={setTemplateId}
        assetOptions={assetOptions}
        selectedCurrency={selectedCurrency}
        hasWithdrawFee={hasWithdrawFee}
        isLoading={isFromDataLoading}
        isError={Boolean(isFormDataError)}
        error={formDataError}
        onMaxClick={handleMaxClick}
        commissionData={commissionData}
        formStepsData={formStepsData}
        currentStepIndex={currentStepIndex}
        savedBankAddresses={bankAddressOptions}
        onEditSavedBankAddress={handleEditSavedBankAddress}
        countries={countriesData?.value || []}
      />
      {error && <ApiErrorText error={error} className="py-0" />}
    </ModalComponentWrapper>
  );
};
